<template>
  <label :aria-label class="group flex items-center gap-12">
    <input
      :checked="isChecked"
      class="peer sr-only"
      :name="value"
      v-bind="inputAttrs"
      type="checkbox"
      @change="handleChange"
    />
    <span :class="chipClasses">
      <component :is="icon" class="shrink-0" :class="iconClasses" />
      <span class="flex items-baseline gap-2">
        <span class="line-clamp-1 break-all">{{ label }}</span>
        <span v-if="count" :class="countClasses"> ({{ count }}) </span>
      </span>
    </span>
  </label>
</template>

<script lang="ts" setup>
import { type Component, computed, useAttrs } from 'vue'

import type { I18nValues } from '@backmarket/nuxt-module-i18n/types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { tw } from '@backmarket/utils/string/tw'

import translations from './AITags.translations'

const attrs = useAttrs()
const modelValue = defineModel<string[]>({ default: () => [] })
const i18n = useI18n()

const {
  variant = 'neutral',
  value,
  label,
  count,
} = defineProps<{
  icon: Component
  variant?: 'positive' | 'negative' | 'neutral'
  label: string
  count?: number
  value: string
}>()

const inputAttrs = computed(() => {
  const { id, ...rest } = attrs

  return rest
})

const emit = defineEmits<{
  /**
   * Emitted when the Checkbox is checked or unchecked.
   * Bound through the use of `@change`.
   */
  change: [Event]
}>()

const isChecked = computed(() => {
  return modelValue.value?.includes(value) ?? false
})

const ariaLabel = computed(() =>
  i18n(translations.tagAlternativeText, {
    tag: label,
    sentiment: variant,
    count: count ?? 0,
  } as unknown as I18nValues),
)

function handleChange(event: Event) {
  emit('change', event)

  const target = event.target as HTMLInputElement
  const currentValue = modelValue.value ?? []

  if (target.checked) {
    modelValue.value = [...currentValue, value]
  } else {
    modelValue.value = currentValue.filter((v) => v !== value)
  }
}

const iconClasses = computed(() => ({
  'text-static-success-hi': variant === 'positive' || variant === 'neutral',
  'text-static-danger-hi': variant === 'negative',
  'text-static-light-hi': isChecked.value,
}))

const countClasses = computed(() => [
  tw`caption`,
  isChecked.value ? tw`text-static-light-hi` : tw`text-static-default-low`,
])

const chipClasses = computed(() => [
  isChecked.value
    ? tw`bg-action-default-low-pressed border-action-default-mid-pressed text-onaction-default-low-pressed hover:bg-action-default-low-hoverpressed peer-focus-visible:bg-action-default-low-hoverpressed`
    : tw`bg-action-default-low border-action-default-mid text-onaction-default-low hover:bg-action-default-low-hover peer-focus-visible:bg-action-default-low-hover`,
  tw`outline-2 outline-offset-4 peer-focus-visible:outline-default-hi`,
  tw`motion-safe:transition motion-safe:duration-[225ms]`,
  tw`cursor-pointer rounded-full border-1 label-large h-40 flex items-center gap-4 px-16`,
])
</script>
